import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import teamMembersData from '@/data/team-members.json';

interface TeamMember {
  id: string
  name: string
  position: string
  bio: string | null
  image_url: string | null
  email: string | null
  linkedin_url: string | null
  display_order: number
  is_active: boolean
}

const TeamSection = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    // Load static data
    const data = (teamMembersData as TeamMember[])
      .filter(m => m.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setTeamMembers(data)
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-96 mx-auto mb-16"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-24 bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-20 animate-on-scroll">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">Meet Our Team</h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            Our diverse team of experts brings together years of experience and a shared passion for technology.
          </p>
        </div>
        
        {teamMembers.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-slate-400" />
            </div>
            <p className="text-slate-500 text-lg">No team members found. Please run the data fetch script to populate content.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div 
                key={member.id}
                className="group relative aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 animate-on-scroll cursor-pointer"
                style={{ animationDelay: `${Math.floor(index / 4) * 0.1}s` }}
              >
                {/* Team member image */}
                <img 
                  src={member.image_url || "/placeholder.svg"} 
                  alt={member.name} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                  <div className="text-center text-white p-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                    <p className="text-slate-200 font-medium mb-3">{member.position}</p>
                    {member.bio && (
                      <p className="text-slate-300 text-sm leading-relaxed line-clamp-3">{member.bio}</p>
                    )}
                    {(member.email || member.linkedin_url) && (
                      <div className="flex justify-center space-x-3 mt-4">
                        {member.email && (
                          <a 
                            href={`mailto:${member.email}`} 
                            className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-xs">✉</span>
                          </a>
                        )}
                        {member.linkedin_url && (
                          <a 
                            href={member.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-xs">in</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TeamSection;
